import type { FetchPageServiceParameters } from "~/core/composables/useFetchPageV2";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import organizationService from "~/modules/organization/services/organization.service";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";

export class UserGroupService extends RestService {
  protected USER_GROUPS = "userGroups";
  protected UNASSIGNED_USER_GROUPS = "unassignedUserGroups";

  /**
   * Get a paginated user groups by organization
   * @param organizationCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  getOrganizationUserGroups({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<GroupsUserV2>> {
    const groupsParams = {
      page,
      pageSize,
      filter: searchParams,
      sort,
      ...(params ? Object.fromEntries(params) : {}),
    };

    return this.$apiV2.get(
      this.getOrganizationUserGroupsPath(organizationCode),
      {
        params: groupsParams,
      },
    );
  }

  /**
   * Get a paginated user groups by organization and sector
   * @param organizationCode
   * @param sectorCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  getOrganizationSectorUserGroups({
    organizationCode,
    sectorCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<GroupsUserV2>> {
    return this.$apiV2.get(
      this.getOrganizationSectorPath(organizationCode, sectorCode),
      {
        params: {
          page,
          pageSize,
          filter: searchParams,
          sort,
          ...(params ? Object.fromEntries(params) : {}),
        },
      },
    );
  }

  /**
   * Get a paginated user groups by organization and sector
   * @param organizationCode
   * @param sectorCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  getOrganizationSectorUnassignedUserGroups({
    organizationCode,
    sectorCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<GroupsUserV2>> {
    return this.$apiV2.get(
      this.getOrganizationSectorPath(organizationCode, sectorCode, true),
      {
        params: {
          page,
          pageSize,
          filter: searchParams,
          sort,
          ...(params ? Object.fromEntries(params) : {}),
        },
      },
    );
  }

  /**
   * Get a user group by organization and code
   * @param organizationCode
   * @param groupCode group code
   */
  getOrganizationUserGroupByCode(
    organizationCode: number,
    groupCode: string | number,
  ): Promise<GroupsUserV2> {
    return this.$apiV2.get(
      `${this.getOrganizationUserGroupsPath(organizationCode)}/${groupCode}`,
    );
  }

  /**
   * @deprecated To be contextualized by organization
   * Get a list of user groups by codes
   * @param codes
   */
  getUserGroupsByCode(
    organizationCode: number,
    codes: string[],
  ): Promise<UserGroup[]> {
    const data = {
      codes,
    };
    const config = {
      headers: {
        "Content-type": "application/json",
        Accept: "application/json",
      },
    };
    return this.$api.post(
      `${this.getOrganizationUserGroupsPath(organizationCode)}/getUserGroupsByCodes`,
      data,
      config,
    );
  }

  /**
   * Create a user group in an organization
   * @param organizationCode
   * @param userGroup
   */
  createOrganizationUserGroup(
    organizationCode: number,
    userGroup: GroupsUserV2,
  ): Promise<string> {
    return this.$apiV2.post(
      this.getOrganizationUserGroupsPath(organizationCode),
      userGroup,
    );
  }

  /**
   * Update a user group of an organization
   * @param organizationCode
   * @param userGroupOldName the group name before the update (used as ID)
   * @param userGroup the group details
   */
  updateOrganizationUserGroup(
    organizationCode: number,
    userGroupOldName: string,
    userGroup: GroupsUserV2,
  ): Promise<UserGroup> {
    return this.$apiV2.put(
      `${this.getOrganizationUserGroupsPath(organizationCode)}/${userGroupOldName}`,

      userGroup,
    );
  }

  /**
   * Remove a user group from an organization
   * @param organizationCode
   * @param groupName
   */
  removeOrganizationUserGroup(organizationCode: number, groupName: string) {
    return this.$apiV2.delete(
      `${this.getOrganizationUserGroupsPath(organizationCode)}/${groupName}`,
    );
  }

  private getOrganizationUserGroupsPath(organizationCode: number | undefined) {
    return `${organizationService.getOrganizationPath(organizationCode)}/${this.USER_GROUPS}`;
  }

  private getOrganizationSectorPath(
    organizationCode?: number,
    sectorCode?: number,
    unassignedUserGroups?: boolean,
  ) {
    return `${organizationService.getOrganizationPath(organizationCode)}/sectors/${sectorCode}/${unassignedUserGroups ? this.UNASSIGNED_USER_GROUPS : this.USER_GROUPS}`;
  }
}

export default new UserGroupService();
